import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { X, Lock, TrendingUp, Zap, ArrowRight, Infinity } from 'lucide-react';

/**
 * QuotaUpgradeModal - Shows when user exceeds their quota limit
 * Provides clear messaging about what happened and how to upgrade
 */
export default function QuotaUpgradeModal({ 
  isOpen, 
  onClose, 
  quotaType,
  used,
  limit,
  planName,
  role
}) {
  if (!isOpen) return null;

  // Quota type labels and descriptions
  const quotaInfo = {
    investor_previews: {
      label: 'Investor Previews',
      icon: '👀',
      description: 'View investor profiles to find the right match',
      upgradeMessage: 'Get unlimited investor previews with a Pro plan'
    },
    match_suggestions: {
      label: 'Match Suggestions',
      icon: '✨',
      description: 'AI-powered recommendations for compatible partners',
      upgradeMessage: 'Unlock unlimited match suggestions'
    },
    browsing: {
      label: 'Profile Browsing',
      icon: '🔍',
      description: 'Browse startup profiles in detail',
      upgradeMessage: 'Browse unlimited startup profiles with Pro'
    },
    pitch_deck_previews: {
      label: 'Pitch Deck Views',
      icon: '📄',
      description: 'Review founder pitch decks',
      upgradeMessage: 'Get unlimited pitch deck access'
    },
    messaging_initiations: {
      label: 'Message Initiations',
      icon: '💬',
      description: 'Start conversations with potential matches',
      upgradeMessage: 'Send unlimited messages with Pro'
    }
  };

  const info = quotaInfo[quotaType] || {
    label: quotaType?.replace(/_/g, ' ') || 'This feature',
    icon: '🔒',
    description: 'This feature has usage limits',
    upgradeMessage: 'Upgrade to unlock more'
  };

  // Determine upgrade path based on role
  const getUpgradePath = () => {
    if (role === 'founder') return '/pricing/founder';
    if (role === 'investor') return '/pricing/investor';
    if (role === 'cofounder') return '/pricing/cofounder';
    return '/pricing';
  };

  // Get recommended plan based on role
  const getRecommendedPlan = () => {
    if (role === 'founder') return { name: 'Founder Pro', price: 49 };
    if (role === 'investor') return { name: 'Investor Pro', price: 249 };
    if (role === 'cofounder') return { name: 'Co-Founder Plus', price: 19 };
    return { name: 'Pro', price: 49 };
  };

  const recommendedPlan = getRecommendedPlan();

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      data-testid="quota-upgrade-modal"
    >
      <Card className="bg-white border border-slate-200 shadow-2xl max-w-md w-full p-0 relative overflow-hidden">
        {/* Header with gradient */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-8 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            data-testid="close-upgrade-modal"
          >
            <X size={20} />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center text-2xl">
              {info.icon}
            </div>
            <div>
              <h2 className="text-xl font-bold">Monthly Limit Reached</h2>
              <p className="text-blue-100 text-sm mt-1">{info.label}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {/* Usage summary */}
          <div className="bg-slate-50 rounded-lg p-4 mb-6 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <span className="text-slate-600 text-sm">Your usage this month</span>
              <Lock className="text-slate-400" size={16} />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1 bg-slate-200 rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-red-500 h-full rounded-full transition-all"
                  style={{ width: '100%' }}
                />
              </div>
              <span className="text-slate-700 font-semibold text-sm whitespace-nowrap">
                {used} / {limit}
              </span>
            </div>
            <p className="text-slate-500 text-xs mt-2">
              {info.description}
            </p>
          </div>

          {/* Upgrade card */}
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Zap className="text-blue-600" size={20} />
              </div>
              <div className="flex-1">
                <h3 className="text-blue-900 font-semibold">{recommendedPlan.name}</h3>
                <p className="text-blue-700 text-sm mt-1">{info.upgradeMessage}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Infinity className="text-blue-600" size={16} />
                  <span className="text-blue-800 text-sm font-medium">Unlimited {info.label.toLowerCase()}</span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-blue-900 font-bold text-lg">${recommendedPlan.price}</span>
                <span className="text-blue-600 text-sm">/mo</span>
              </div>
            </div>
          </div>

          {/* Current plan note */}
          <p className="text-center text-slate-500 text-xs mb-4">
            Current plan: <span className="font-medium text-slate-700">{planName || 'Free'}</span>
          </p>

          {/* Action buttons */}
          <div className="flex flex-col gap-3">
            <Link to={getUpgradePath()}>
              <Button 
                className="btn-primary w-full flex items-center justify-center gap-2"
                data-testid="upgrade-now-button"
              >
                <TrendingUp size={18} />
                Upgrade Now
                <ArrowRight size={18} />
              </Button>
            </Link>
            <Button 
              variant="ghost" 
              onClick={onClose} 
              className="text-slate-600 hover:text-slate-800"
            >
              Maybe Later
            </Button>
          </div>

          {/* Benefit note */}
          <p className="text-center text-slate-400 text-xs mt-4">
            Quotas reset at the start of each month
          </p>
        </div>
      </Card>
    </div>
  );
}
